'use client';

import { useState } from 'react';
import { createLead } from '@/services/leadService';

interface EmailCaptureSectionProps {
  onSuccess?: (email: string) => void;
}

const EmailCaptureSection = ({ onSuccess }: EmailCaptureSectionProps) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    if (!validateEmail(email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    const { data, error } = await createLead({
      email: email.trim().toLowerCase(),
      source: 'landing_page_early_access'
    });

    setIsSubmitting(false);

    if (error) {
      setMessage({ type: 'error', text: error });
    } else {
      setMessage({
        type: 'success',
        text: "You're on the list! Early access is coming soon 🎉"
      });
      if (onSuccess) onSuccess(email.trim().toLowerCase());
      setEmail('');
    }
  };

  return (
    <section data-component-id="src/app/landing-page/components/EmailCaptureSection.tsx:56:4" data-component-path="src/app/landing-page/components/EmailCaptureSection.tsx" data-component-line="56" data-component-file="EmailCaptureSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20px-4%20bg-gradient-to-br%20from-cyan-50%20to-purple-50%22%7D" className="py-16 px-4 bg-gradient-to-br from-cyan-50 to-purple-50">
      <div data-component-id="src/app/landing-page/components/EmailCaptureSection.tsx:57:6" data-component-path="src/app/landing-page/components/EmailCaptureSection.tsx" data-component-line="57" data-component-file="EmailCaptureSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20text-center%22%7D" className="max-w-4xl mx-auto text-center">
        
        <h2 data-component-id="src/app/landing-page/components/EmailCaptureSection.tsx:59:8" data-component-path="src/app/landing-page/components/EmailCaptureSection.tsx" data-component-line="59" data-component-file="EmailCaptureSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20md%3Atext-4xl%20font-bold%20text-gray-900%20mb-4%22%2C%22textContent%22%3A%22Join%20the%20Waitlist%22%7D" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Join the Waitlist
        </h2>

        <p data-component-id="src/app/landing-page/components/EmailCaptureSection.tsx:63:8" data-component-path="src/app/landing-page/components/EmailCaptureSection.tsx" data-component-line="63" data-component-file="EmailCaptureSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-gray-600%20mb-8%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Be%20among%20the%20first%20to%20transform%20your%20screen%20time%20into%20learning%20time.%20%5Cn%20%20%20%20%20%20%20%20%20%20Get%20early%20access%20and%20exclusive%20launch%20benefits.%22%7D" className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
          Be among the first to transform your screen time into learning time. 
          Get early access and exclusive launch benefits.
        </p>

        <form data-component-id="src/app/landing-page/components/EmailCaptureSection.tsx:68:8" data-component-path="src/app/landing-page/components/EmailCaptureSection.tsx" data-component-line="68" data-component-file="EmailCaptureSection.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22max-w-md%20mx-auto%22%7D" onSubmit={handleSubmit} className="max-w-md mx-auto">
          <div data-component-id="src/app/landing-page/components/EmailCaptureSection.tsx:69:10" data-component-path="src/app/landing-page/components/EmailCaptureSection.tsx" data-component-line="69" data-component-file="EmailCaptureSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20gap-3%22%7D" className="flex flex-col sm:flex-row gap-3">
            <input data-component-id="src/app/landing-page/components/EmailCaptureSection.tsx:70:12" data-component-path="src/app/landing-page/components/EmailCaptureSection.tsx" data-component-line="70" data-component-file="EmailCaptureSection.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22email%22%2C%22value%22%3A%22%5Bvar%3Aemail%5D%22%2C%22className%22%3A%22flex-1%20px-6%20py-4%20rounded-xl%20border-2%20border-gray-200%20focus%3Aborder-cyan-500%20focus%3Aoutline-none%20text-gray-900%20placeholder-gray-400%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20transition-colors%22%7D"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={isSubmitting}
              className="flex-1 px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-cyan-500 focus:outline-none text-gray-900 placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            />

            <button data-component-id="src/app/landing-page/components/EmailCaptureSection.tsx:79:12" data-component-path="src/app/landing-page/components/EmailCaptureSection.tsx" data-component-line="79" data-component-file="EmailCaptureSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22px-8%20py-4%20bg-gradient-to-r%20from-purple-700%2F80%20to-purple-900%2F80%20text-white%20font-semibold%20rounded-xl%20hover%3Afrom-purple-800%2F80%20hover%3Ato-purple-950%2F80%20transition-all%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20shadow-lg%20transform%20hover%3Ascale-105%22%7D"
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-4 bg-gradient-to-r from-purple-700/80 to-purple-900/80 text-white font-semibold rounded-xl hover:from-purple-800/80 hover:to-purple-950/80 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transform hover:scale-105"
            >
              {isSubmitting ? 'Joining...' : 'Join Waitlist'}
            </button>
          </div>

          {message && (
            <div data-component-id="src/app/landing-page/components/EmailCaptureSection.tsx:89:12" data-component-path="src/app/landing-page/components/EmailCaptureSection.tsx" data-component-line="89" data-component-file="EmailCaptureSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              className={`mt-4 p-4 rounded-lg ${
                message.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}
            >
              <p data-component-id="src/app/landing-page/components/EmailCaptureSection.tsx:96:14" data-component-path="src/app/landing-page/components/EmailCaptureSection.tsx" data-component-line="96" data-component-file="EmailCaptureSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-medium%22%7D" className="text-sm font-medium">{message.text}</p>
            </div>
          )}
        </form>

        <p data-component-id="src/app/landing-page/components/EmailCaptureSection.tsx:101:8" data-component-path="src/app/landing-page/components/EmailCaptureSection.tsx" data-component-line="101" data-component-file="EmailCaptureSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-6%20text-sm%20text-gray-500%22%2C%22textContent%22%3A%22%F0%9F%94%92%20Your%20email%20is%20safe%20with%20us.%20No%20spam%2C%20ever.%22%7D" className="mt-6 text-sm text-gray-500">
          🔒 Your email is safe with us. No spam, ever.
        </p>
      </div>
    </section>
  );
};

export default EmailCaptureSection;
